import { Box } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import type { ITabConfig } from '@src/App/Components/Core/Layouts/LayoutElements/MvpTabContainer/ITabConfig';
import MvpSubNav, { type IMvpQuickAction } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useNavigation } from '@src/App/Components/Core/Navigation/NavigationContext';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AnalyseCvTab } from './AnalayseCvTab';
import { CertificatesTab } from './CertificatesTab';
import { DevelopmentTab } from './DevelopmentTab';
import { ForecastTab } from './ForecastTab';
import { GeneralTab } from './GeneralTab';
import { RolesTab } from './RolesTab';
import { SkillsTab } from './SkillsTab';

export enum EProfileSettingsTabs {
    GENERAL = 0,
    ANALYSE = 1,
    FORECAST = 2,
    SKILLS = 3,
    CERTIFICATES = 4,
    DEVELOPMENT = 5,
    ROLES = 6,
}

/**
 * Component representing the ProfileSettings.
 *
 * @returns JSX.Element
 */
function ProfileSettings({ tabId, urlSuffix }: { tabId: EProfileSettingsTabs; urlSuffix?: 'SUGGESTIONS' }) {
    const { setSubNav } = useNavigation();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);

    const mainTabs = useMemo(() => {
        return [
            {
                icon: getAppIcon(EIconCategory.GENERAL),
                label: langStrings.general,
                url: `/${ELinks.PROFILE_EDIT}`,
                key: EProfileSettingsTabs.GENERAL,
                render: <GeneralTab />,
            },

            {
                icon: getAppIcon(EIconCategory.CV_ANALYSE),
                label: langStrings.analyse,
                url: `/${ELinks.PROFILE_EDIT_ANALYSE}`,
                key: EProfileSettingsTabs.ANALYSE,
                render: <AnalyseCvTab />,
            },
            {
                icon: getAppIcon(EIconCategory.TAB_FORECAST),
                label: langStrings.forecast,
                url: `/${ELinks.PROFILE_EDIT_FORECAST}`,
                key: EProfileSettingsTabs.FORECAST,
                render: <ForecastTab />,
            },
            ...(readRoles
                ? [
                      {
                          icon: getAppIcon(EIconCategory.ROLES),
                          label: langStrings.oftenUsed.roles,
                          url: `/${ELinks.PROFILE_EDIT_ROLES}`,
                          key: EProfileSettingsTabs.ROLES,
                          render: <RolesTab />,
                      },
                  ]
                : []),
            {
                icon: getAppIcon(EIconCategory.SKILLS),
                label: langStrings.oftenUsed.skills,
                url: `/${ELinks.PROFILE_EDIT_SKILLS}`,
                key: EProfileSettingsTabs.SKILLS,
                render: <SkillsTab showSuggestionsPage={urlSuffix === 'SUGGESTIONS'} />,
            },
            {
                icon: getAppIcon(EIconCategory.CERTIFICATES),
                label: langStrings.oftenUsed.certificates,
                url: `/${ELinks.PROFILE_EDIT_CERTIFICATES}`,
                key: EProfileSettingsTabs.CERTIFICATES,
                render: <CertificatesTab />,
            },
            {
                icon: getAppIcon(EIconCategory.DEVELOPMENT_GOALS),
                label: langStrings.development,
                url: `/${ELinks.PROFILE_EDIT_DEVELOPMENT}`,
                key: EProfileSettingsTabs.DEVELOPMENT,
                render: <DevelopmentTab />,
            },
        ];
    }, [langStrings, readRoles, urlSuffix]);

    useEffect(() => {
        // Konvertiere die Tabs in das ITabConfig-Format für die Subnavigation
        const subNavTabs: ITabConfig[] = mainTabs.map((tab) => {
            return {
                title: tab.label,
                link: tab.url,
                icon: tab.icon,
            };
        });

        // Registriere die Tabs für die Subnavigation im Header
        setSubNav({ tabs: subNavTabs });

        // Cleanup when component unmounts
        return () => setSubNav(null);
    }, [mainTabs, setSubNav]);

    const activeMainTab = mainTabs.find((mt) => mt.key === tabId);

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'back-to-profile',
            label: 'Zurück zum Profil',
            icon: getAppIcon(EIconCategory.PROFILE),
            onClick: () => navigate(`/${ELinks.PROFILE}`),
            variant: 'primary',
        },
        ...(tabId === EProfileSettingsTabs.SKILLS
            ? [
                  {
                      id: 'skills-catalog',
                      label: 'Fähigkeiten Katalog',
                      icon: getAppIcon(EIconCategory.CATALOG),
                      onClick: () => navigate(`/${ELinks.SKILLS}`),
                  },
              ]
            : []),
    ];

    return (
        <InnerLayout hideHeader>
            <MvpSubNav quickActions={quickActions} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    mt: 0,
                }}
            >
                {activeMainTab ? activeMainTab.render : <LoadingBox />}
            </Box>
        </InnerLayout>
    );
}

export default ProfileSettings;
